/**
 * src/components/WorkDetailsEditor.tsx
 * 複数作業明細（work_details）の編集UI。
 *
 * ReportFormModal.tsx の作業明細ブロックと同等のロジックを、画像アップロードを含まない
 * 形で抽出した再利用可能コンポーネント。ヘルプスタッフの公開提出フォーム（HelpSubmit.tsx）と
 * 管理者のヘルプ勤務編集（HelpStaff.tsx）の両方から使う。
 *
 * ReportFormModal.tsx 自体は変更しない（通常アシスタントの日報フローへの回帰リスクを避けるため）。
 */

import {
  STAGE_OPTIONS,
  KAIKAI_KIKI_ID,
  KAIKAI_KIKI_LABEL,
  isKaikaiKiki,
  emptyDetail,
  type WorkDetail,
} from './reportUtils'

type WorkOption = { id: string; title: string }

export function WorkDetailsEditor({ details, onChange, works = [], disabled = false, errorMessage }: {
  details: WorkDetail[]
  onChange: (details: WorkDetail[]) => void
  works?: WorkOption[]
  disabled?: boolean
  errorMessage?: string
}) {
  const setDetail = (idx: number, key: keyof WorkDetail, val: string) =>
    onChange(details.map((d, i) => (i === idx ? { ...d, [key]: val } : d)))

  const setWorkForDetail = (idx: number, workId: string) => {
    if (workId === KAIKAI_KIKI_ID) {
      onChange(details.map((d, i) =>
        i === idx
          ? { ...d, work_id: KAIKAI_KIKI_ID, work_title: KAIKAI_KIKI_LABEL,
              episode_number: '', stage: '', pages_done: '' }
          : d
      ))
    } else {
      const w = works.find((w) => w.id === workId)
      onChange(details.map((d, i) =>
        i === idx ? { ...d, work_id: workId, work_title: w?.title ?? '' } : d
      ))
    }
  }

  const addDetail = () => onChange([...details, emptyDetail()])
  const removeDetail = (idx: number) => {
    if (details.length === 1) return
    onChange(details.filter((_, i) => i !== idx))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
            作業明細 <span style={{ color: 'var(--red)' }}>*</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
            作品・工程・ページ数・作業内容を入力してください
          </div>
        </div>
      </div>
      {errorMessage && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>{errorMessage}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {details.map((d, idx) => {
          const kk = isKaikaiKiki(d.work_id, d.work_title)
          return (
            <div key={idx} style={{
              background: kk ? 'var(--amber-light)' : 'var(--paper-2)',
              borderRadius: 8, padding: '12px 14px',
              border: `1px solid ${kk ? 'rgba(176,122,18,0.3)' : 'var(--paper-3)'}`,
            }}>
              {/* 行ヘッダー */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)' }}>作業 {idx + 1}</span>
                {!disabled && details.length > 1 && (
                  <button type="button" onClick={() => removeDetail(idx)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 12, color: 'var(--red)', padding: '2px 6px' }}>
                    ✕ 削除
                  </button>
                )}
              </div>

              {/* 作品選択 */}
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label>作品名</label>
                {works.length > 0 ? (
                  <select
                    value={kk ? KAIKAI_KIKI_ID : (d.work_id ?? '')}
                    onChange={(e) => setWorkForDetail(idx, e.target.value)}
                    disabled={disabled}
                  >
                    <option value="">— 選択してください —</option>
                    {works.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
                    <option value={KAIKAI_KIKI_ID}>{KAIKAI_KIKI_LABEL}</option>
                  </select>
                ) : (
                  <input type="text" value={d.work_title ?? ''} disabled={disabled}
                    onChange={(e) => setDetail(idx, 'work_title', e.target.value)}
                    placeholder="作品名を入力" />
                )}
              </div>

              {/* 話数・工程・ページ（カイカイキキ時はdisabled） */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10,
                opacity: kk ? 0.4 : 1,
              }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>話数</label>
                  <input type="number" inputMode="numeric"
                    value={d.episode_number ?? ''} disabled={kk || disabled}
                    onChange={(e) => !kk && setDetail(idx, 'episode_number', e.target.value)}
                    placeholder="例: 6" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>工程</label>
                  <select value={d.stage ?? ''} disabled={kk || disabled}
                    onChange={(e) => !kk && setDetail(idx, 'stage', e.target.value)}>
                    <option value="">—</option>
                    {STAGE_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>完了ページ数</label>
                  <input type="number" inputMode="numeric"
                    value={d.pages_done ?? ''} disabled={kk || disabled}
                    onChange={(e) => !kk && setDetail(idx, 'pages_done', e.target.value)}
                    placeholder="例: 3" />
                </div>
              </div>
              {kk && (
                <div style={{ fontSize: 11, color: 'var(--amber)', marginBottom: 8 }}>
                  ⚠ カイカイキキ業務は話数・工程・ページ数の入力不要です
                </div>
              )}

              {/* 作業内容 */}
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label>
                  作業内容
                  {kk && <span style={{ color: 'var(--red)', marginLeft: 4 }}>*</span>}
                </label>
                <textarea
                  value={d.memo ?? ''}
                  disabled={disabled}
                  onChange={(e) => setDetail(idx, 'memo', e.target.value)}
                  placeholder={
                    kk
                      ? '例：\n・商品画像切り抜き 30点\n・チェック作業 2時間'
                      : '例：\n・教室背景、モブ配置まで完了\n・次回は廊下シーンから'
                  }
                  style={{ minHeight: kk ? 72 : 56 }}
                />
              </div>

              {/* 作業データ保存先 */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ marginBottom: 4 }}>作業データ保存先（任意）</label>
                <input
                  type="text"
                  value={d.work_data_path ?? ''}
                  disabled={disabled}
                  onChange={(e) => setDetail(idx, 'work_data_path', e.target.value)}
                  placeholder="例：\\192.168.40.197\disk1\漫画\作品名\〇話"
                  maxLength={500}
                />
              </div>
            </div>
          )
        })}
      </div>

      {!disabled && (
        <button type="button" onClick={addDetail} className="btn btn-outline btn-sm" style={{ marginTop: 10 }}>
          ＋ 作業を追加
        </button>
      )}
    </div>
  )
}
